// The credit is HARD-CAPPED at the measured organic P pool (it can never credit more P than the
// soil actually holds in organic form).
import { ordinaryKrige } from "./kriging";
import type { HaneyPoint } from "./haney";

const clamp = (x: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, x));
const num = (v: number | null | undefined): v is number =>
  v != null && Number.isFinite(v);

// Fitted structure — documented + tunable; the 20:1 zero-credit threshold is spec.
export const PMIN = {
  CN_THRESHOLD: 20, // organic C:N AT OR ABOVE this → zero P mineralization credited.
  CN_FULL: 8, // organic C:N at/below which the C:N factor is 1 (fully N-rich substrate)
  CO2_REF: 100, // CO₂-C (ppm) at/above which the respiration factor is 1 (fully active)
  // fix: WITHOUT this ceiling the modulated fraction reached 1.0 = crediting the ENTIRE organic P
  // pool in a single season, which is agronomically impossible (only a small, labile part of soil
  // organic P turns over per year) — it produced absurd credits (~500+ lb P₂O₅/ac on a ~90
  // ppm-organic-P soil) that zeroed out the whole P rec. The seasonal ceiling scales the
  // pool-fraction to a realistic annual mineralization rate; C:N and CO₂ then modulate DOWN from
  // it.
  SEASON_MAX_FRACTION: 0.15,
  POOL_LB_PER_PPM: 2.4, // acre-8" furrow slice: 1 ppm = 2.4 lb/ac (matches the platform's 8" basis)
  P_TO_P2O5: 2.2914, // elemental P → P₂O₅
} as const;

// Fraction of the organic P pool credited as mineralized THIS SEASON (0..SEASON_MAX_FRACTION). Rises as
// organic C:N drops below 20:1 AND as CO₂ respiration rises, but is CAPPED at the realistic seasonal ceiling
// (SEASON_MAX_FRACTION) — never the whole pool. Exactly 0 at/above 20:1 (hard). Missing C:N or CO₂ → 0.
export function pMineralizationFraction(
  cn: number | null | undefined,
  co2c: number | null | undefined,
): number {
  if (!num(cn) || cn >= PMIN.CN_THRESHOLD) return 0; // ≥ 20:1 (or unknown C:N) → immobilization → 0
  const gCn = clamp(
    (PMIN.CN_THRESHOLD - cn) / (PMIN.CN_THRESHOLD - PMIN.CN_FULL),
    0,
    1,
  ); // 0 at 20 → 1 at 8
  const gCo2 = num(co2c) ? clamp(co2c / PMIN.CO2_REF, 0, 1) : 0; // no respiration data → no credit
  return clamp(
    PMIN.SEASON_MAX_FRACTION * gCn * gCo2,
    0,
    PMIN.SEASON_MAX_FRACTION,
  ); // seasonal ceiling, then modulate down
}

// Natural P mineralization credit as **P₂O₅ lb/ac**, HARD-CAPPED at the organic P pool (the credit
// can never exceed the measured organic P — ). 0 when inputs are missing or the C:N is ≥ 20:1.
export function pMineralizationCredit(
  organicP_ppm: number | null | undefined,
  cn: number | null | undefined,
  co2c: number | null | undefined,
): number {
  if (!num(organicP_ppm) || organicP_ppm <= 0) return 0;
  const poolP2O5 = organicP_ppm * PMIN.POOL_LB_PER_PPM * PMIN.P_TO_P2O5; // organic P pool as P₂O₅ lb/ac
  const credit = poolP2O5 * pMineralizationFraction(cn, co2c);
  return Math.min(poolP2O5, credit); // hard cap: never exceeds the measured organic P pool
}

// Per-cell P mineralization credit (P₂O₅ lb/ac) for the fertility engine: ordinary-krige the three
// Haney parameters onto the cell centroids, then apply the credit formula per cell. Aligned to the
// same cell grid the recs use. Always a NUMBER — **0** where no credit is due (immobilizing C:N ≥ 20
// missing respiration, or no krigeable organic P); computeRecs + the provenance label both key on > 0.
export function perCellPMinCredit(
  points: HaneyPoint[],
  centroids: { lng: number; lat: number }[],
): number[] {
  const krige = (key: string) =>
    ordinaryKrige(
      points
        .filter((p) => num(p.values[key]))
        .map((p) => ({ lng: p.lon, lat: p.lat, value: p.values[key] })),
      centroids,
    );
  const orgP = krige("organic_p");
  const cn = krige("weoc_weon");
  const co2 = krige("co2c");
  return centroids.map((_, i) => pMineralizationCredit(orgP[i], cn[i], co2[i]));
}
